/**
 * The formal content schema — the single source of truth for what a piece of
 * learning content may look like. English, Python and TypeScript all share it:
 * there is no per-subject format, and no subject-specific branch anywhere in
 * the app.
 *
 * Nothing reaches the UI without passing through here. That is the whole point
 * of the architecture: the UI cannot tell whether a lesson was hand-written,
 * generated by an AI, or fetched from a backend — it only ever receives a
 * validated `Lesson`.
 */
import { v, type Ctx, type Infer, type Schema } from './validation.ts';

/* ────────────────────────────────────────────────────────────── primitives */

/** Stable, URL-safe identifier: `python-functions-basics`, `functions-001`. */
export const IdSchema = v
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:[-_][a-z0-9]+)*$/, 'must be a lowercase slug, e.g. "python-functions-basics"');

/**
 * Languages the app speaks. Two independent settings use this: the interface
 * language, and the language lessons are delivered in. They are separate on
 * purpose — a Polish speaker studying English wants a Polish interface and
 * English sentences.
 */
export const LANGUAGES = ['en', 'pl'] as const;
export const LanguageSchema = v.enum(LANGUAGES);
export type Language = (typeof LANGUAGES)[number];
export const DEFAULT_LANGUAGE: Language = 'en';

export const LANGUAGE_NAMES: Record<Language, string> = { en: 'English', pl: 'Polski' };

export const LEVELS = ['beginner', 'intermediate', 'advanced'] as const;
export const LevelSchema = v.enum(LEVELS);
export type Level = (typeof LEVELS)[number];

/** Ordering used whenever levels are compared — see `levelAtLeast`. */
export const LEVEL_ORDER: Record<Level, number> = { beginner: 0, intermediate: 1, advanced: 2 };

export const EXERCISE_TYPES = [
  'multiple_choice',
  'true_false',
  'fill_blank',
  'translation',
  'flashcard',
  'code_multiple_choice',
  'code_completion',
  'find_error',
  'concept_question',
] as const;
export const ExerciseTypeSchema = v.enum(EXERCISE_TYPES);
export type ExerciseType = (typeof EXERCISE_TYPES)[number];

export const CODE_LANGUAGES = ['python', 'typescript', 'javascript', 'sql', 'text'] as const;
export const CodeLanguageSchema = v.enum(CODE_LANGUAGES);
export type CodeLanguage = (typeof CODE_LANGUAGES)[number];

/**
 * Audio is modelled but not implemented — the MVP has no playback. It hangs
 * off content blocks, exercises and individual answers so listening, speaking
 * and pronunciation work can be added later without reshaping stored content.
 */
export const AudioAssetSchema = v.object({
  url: v.string().min(1),
  locale: v.string().min(2).max(10).optional(),
  voice: v.string().min(1).optional(),
  transcript: v.string().min(1).optional(),
  /** Set when the learner is expected to speak rather than listen. */
  expectsSpeech: v.boolean().optional(),
});
export type AudioAsset = Infer<typeof AudioAssetSchema>;

/** Difficulty: 1 very easy · 2 easy · 3 medium · 4 hard · 5 advanced. */
export const DifficultySchema = v.number().int().min(1).max(5);

/** The marker an exercise uses for the gap the learner has to fill. */
export const BLANK_MARKER = '___';

/* ─────────────────────────────────────────────────────────── content blocks */

const contentBlockMeta = {
  /** Short kicker shown above the body, e.g. "Definition", "Watch out". */
  title: v.string().min(1).max(80),
  body: v.string().min(1).max(2000),
  audio: AudioAssetSchema.optional(),
};

export const TheoryBlockSchema = v.object({
  type: v.literal('theory'),
  ...contentBlockMeta,
  code: v.string().min(1).optional(),
  language: CodeLanguageSchema.optional(),
});

export const ExampleBlockSchema = v.object({
  type: v.literal('example'),
  ...contentBlockMeta,
  code: v.string().min(1),
  language: CodeLanguageSchema,
});

export const CalloutBlockSchema = v.object({
  type: v.literal('callout'),
  ...contentBlockMeta,
  variant: v.enum(['tip', 'warning', 'note'] as const).default('note'),
  /** A callout may carry a snippet too — "watch out, this is what breaks". */
  code: v.string().min(1).optional(),
  language: CodeLanguageSchema.optional(),
});

export const ContentBlockSchema = v.taggedUnion('type', {
  theory: TheoryBlockSchema,
  example: ExampleBlockSchema,
  callout: CalloutBlockSchema,
});
export type ContentBlock = Infer<typeof ContentBlockSchema>;

/* ──────────────────────────────────────────────────────────────── exercises */

export const AnswerSchema = v.object({
  id: v.string().min(1).max(8),
  text: v.string().min(1).max(400),
  audio: AudioAssetSchema.optional(),
});
export type Answer = Infer<typeof AnswerSchema>;

/** Metadata every exercise carries, whoever or whatever produced it. */
const exerciseMeta = {
  id: IdSchema,
  difficulty: DifficultySchema.default(2),
  /** Feed the strengths/weaknesses analysis; also grouped in the summary. */
  tags: v.array(IdSchema).min(1).max(8),
  estimatedSeconds: v.number().int().min(5).max(900).default(30),
  explanation: v.string().min(1).max(1200),
  /** Human-readable topic label, shown in summaries and review lists. */
  topic: v.string().min(1).max(80).optional(),
  hint: v.string().min(1).max(300).optional(),
  audio: AudioAssetSchema.optional(),
};

/** Shared shape of every exercise answered by picking one of the options. */
const choiceShape = {
  answers: v.array(AnswerSchema).min(2).max(6),
  correctAnswer: v.string().min(1).max(8),
};

/** `correctAnswer` must name one of the options actually offered. */
function choiceRules(value: { answers: Answer[]; correctAnswer: string }, ctx: Ctx): void {
  const ids = value.answers.map((a) => a.id);
  if (new Set(ids).size !== ids.length) ctx.issue('answer ids must be unique', ['answers']);
  if (!ids.includes(value.correctAnswer)) {
    ctx.issue(`"${value.correctAnswer}" is not one of the answers [${ids.join(', ')}]`, ['correctAnswer']);
  }
}

const blankMessage = `must contain the blank marker "${BLANK_MARKER}"`;

export const MultipleChoiceExerciseSchema = v
  .object({
    type: v.literal('multiple_choice'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    ...choiceShape,
  })
  .check(choiceRules);

export const ConceptQuestionExerciseSchema = v
  .object({
    type: v.literal('concept_question'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    ...choiceShape,
  })
  .check(choiceRules);

export const TrueFalseExerciseSchema = v.object({
  type: v.literal('true_false'),
  ...exerciseMeta,
  /** The statement the learner judges. */
  question: v.string().min(1).max(600),
  correctAnswer: v.boolean(),
});

export const FillBlankExerciseSchema = v
  .object({
    type: v.literal('fill_blank'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    sentence: v.string().min(1).max(600).includes(BLANK_MARKER, blankMessage),
    ...choiceShape,
  })
  .check(choiceRules);

export const TranslationExerciseSchema = v
  .object({
    type: v.literal('translation'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    sentence: v.string().min(1).max(600),
    sourceLanguage: v.string().min(2).max(10),
    targetLanguage: v.string().min(2).max(10),
    ...choiceShape,
  })
  .check(choiceRules);

export const FlashcardExerciseSchema = v.object({
  type: v.literal('flashcard'),
  ...exerciseMeta,
  /** Optional kicker above the card, e.g. "Vocabulary". */
  question: v.string().min(1).max(200).optional(),
  front: v.string().min(1).max(300),
  back: v.string().min(1).max(400),
});

export const CodeMultipleChoiceExerciseSchema = v
  .object({
    type: v.literal('code_multiple_choice'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    code: v.string().min(1).max(4000),
    language: CodeLanguageSchema,
    ...choiceShape,
  })
  .check(choiceRules);

export const CodeCompletionExerciseSchema = v
  .object({
    type: v.literal('code_completion'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    code: v.string().min(1).max(4000).includes(BLANK_MARKER, blankMessage),
    language: CodeLanguageSchema,
    ...choiceShape,
  })
  .check(choiceRules);

export const FindErrorExerciseSchema = v
  .object({
    type: v.literal('find_error'),
    ...exerciseMeta,
    question: v.string().min(1).max(600),
    code: v.string().min(1).max(4000),
    language: CodeLanguageSchema,
    ...choiceShape,
  })
  .check(choiceRules);

/**
 * Adding an exercise type is: a schema above, an entry here, and one renderer
 * in `ui/exercises`. No change to the session flow, the progress model or the
 * review scheduler.
 */
export const ExerciseSchema = v.taggedUnion('type', {
  multiple_choice: MultipleChoiceExerciseSchema,
  true_false: TrueFalseExerciseSchema,
  fill_blank: FillBlankExerciseSchema,
  translation: TranslationExerciseSchema,
  flashcard: FlashcardExerciseSchema,
  code_multiple_choice: CodeMultipleChoiceExerciseSchema,
  code_completion: CodeCompletionExerciseSchema,
  find_error: FindErrorExerciseSchema,
  concept_question: ConceptQuestionExerciseSchema,
});

export type MultipleChoiceExercise = Infer<typeof MultipleChoiceExerciseSchema>;
export type ConceptQuestionExercise = Infer<typeof ConceptQuestionExerciseSchema>;
export type TrueFalseExercise = Infer<typeof TrueFalseExerciseSchema>;
export type FillBlankExercise = Infer<typeof FillBlankExerciseSchema>;
export type TranslationExercise = Infer<typeof TranslationExerciseSchema>;
export type FlashcardExercise = Infer<typeof FlashcardExerciseSchema>;
export type CodeMultipleChoiceExercise = Infer<typeof CodeMultipleChoiceExerciseSchema>;
export type CodeCompletionExercise = Infer<typeof CodeCompletionExerciseSchema>;
export type FindErrorExercise = Infer<typeof FindErrorExerciseSchema>;
export type Exercise = Infer<typeof ExerciseSchema>;

/* ────────────────────────────────────────────────── lesson / module / subject */

export const LessonSchema = v
  .object({
    id: IdSchema,
    subject: IdSchema,
    module: IdSchema,
    level: LevelSchema,
    title: v.string().min(1).max(120),
    description: v.string().min(1).max(400),
    estimatedMinutes: v.number().int().min(1).max(120),
    order: v.number().int().min(0).default(0),
    tags: v.array(IdSchema).max(12).default(() => []),
    content: v.array(ContentBlockSchema).min(1).max(12),
    exercises: v.array(ExerciseSchema).min(1).max(60),
  })
  .check((lesson, ctx) => {
    const ids = lesson.exercises.map((e) => e.id);
    const duplicates = [...new Set(ids.filter((id, i) => ids.indexOf(id) !== i))];
    if (duplicates.length > 0) ctx.issue(`duplicate exercise ids: ${duplicates.join(', ')}`, ['exercises']);
  });
export type Lesson = Infer<typeof LessonSchema>;

export const ModuleSchema = v.object({
  id: IdSchema,
  subject: IdSchema,
  title: v.string().min(1).max(120),
  description: v.string().min(1).max(400),
  level: LevelSchema,
  order: v.number().int().min(0),
  /** Lesson order inside the module; also the unlock order. */
  lessonIds: v.array(IdSchema).min(1).max(40),
});
export type Module = Infer<typeof ModuleSchema>;

/**
 * A subject is configuration, not logic. Adding React, SQL or Spanish means
 * adding an entry to `content/subjects.json` plus lessons — no new branches.
 */
export const SubjectSchema = v.object({
  id: IdSchema,
  name: v.string().min(1).max(60),
  /** Icon key resolved by the UI's icon map; falls back to `glyph`. */
  icon: v.string().min(1).max(40),
  /** Two/three-letter monogram drawn in the subject tile. */
  glyph: v.string().min(1).max(3),
  description: v.string().min(1).max(300),
  /** One-line blurb used on the onboarding picker. */
  blurb: v.string().min(1).max(120),
  order: v.number().int().min(0),
  /**
   * Per-subject display names for the three canonical levels — "Basics" reads
   * better than "Beginner" for a programming language.
   */
  levelLabels: v.record(LEVELS, v.string().min(1).max(24)).optional(),
  /** Capabilities this subject's content may use; drives future audio UI. */
  features: v.array(v.enum(['code', 'audio', 'speaking'] as const)).default(() => []),
});
export type Subject = Infer<typeof SubjectSchema>;

export const SubjectCatalogSchema = v.array(SubjectSchema).min(1);
export const ModuleCatalogSchema = v.array(ModuleSchema).min(1);

/**
 * Translations for the catalogue itself — subject and module copy.
 *
 * Lessons are translated as whole files validated by `LessonSchema`, because a
 * translated lesson is a lesson. Subjects and modules are a handful of strings
 * each, so an overlay keyed by id is less duplication than a second catalogue.
 * Every field is optional: whatever is missing falls back to English.
 */
export const CatalogTranslationSchema = v.object({
  subjects: v
    .map(
      v.object({
        name: v.string().min(1).max(60).optional(),
        description: v.string().min(1).max(300).optional(),
        blurb: v.string().min(1).max(120).optional(),
        levelLabels: v.record(LEVELS, v.string().min(1).max(24)).optional(),
      }),
    )
    .default(() => ({})),
  modules: v
    .map(
      v.object({
        title: v.string().min(1).max(120).optional(),
        description: v.string().min(1).max(400).optional(),
      }),
    )
    .default(() => ({})),
});
export type CatalogTranslation = Infer<typeof CatalogTranslationSchema>;

/* ───────────────────────────────────────────────────────────────── helpers */

export function levelAtLeast(candidate: Level, required: Level): boolean {
  return LEVEL_ORDER[candidate] >= LEVEL_ORDER[required];
}

export function levelLabel(subject: Pick<Subject, 'levelLabels'>, level: Level): string {
  return subject.levelLabels?.[level] ?? level.charAt(0).toUpperCase() + level.slice(1);
}

/** True for every exercise the learner answers by picking an option. */
export function isChoiceExercise(
  exercise: Exercise,
): exercise is Exclude<Exercise, FlashcardExercise | TrueFalseExercise> {
  return 'answers' in exercise;
}

/** True for exercises graded by the learner rather than by the app. */
export function isSelfGraded(exercise: Exercise): exercise is FlashcardExercise {
  return exercise.type === 'flashcard';
}

/** Code-bearing exercises, for the `CodeBlock` renderer. */
export function hasCode(
  exercise: Exercise,
): exercise is CodeMultipleChoiceExercise | CodeCompletionExercise | FindErrorExercise {
  return 'code' in exercise;
}

export const _schemaTypeGuard: Schema<Lesson> = LessonSchema;
